import React from 'react';
import './ParallaxSections.css';

const ParallaxSections = () => {
  return (
    <div
      className="parallax-wrapper"
      style={{
        '--bg-parallax': `url('${process.env.PUBLIC_URL}/assets/images/Hero-4.jpg')`
      }}
    >
      {/* SECTION 1: How To Start Online Quran Classes In 3 Simple Steps */}
      <section className="parallax-section how-it-works" id="how-it-works">
        <div className="parallax-section__container">
          <div className="how-it-works__badge">HOW IT WORKS</div>

          <h2 className="how-it-works__title">
            How To Start Online Quran Classes In 3 Simple Steps
          </h2>

          <div className="how-it-works__steps-container">
            {/* Step 1 */}
            <div className="how-it-works__step">
              <div className="how-it-works__icon-wrapper">
                <span>1</span>
              </div>
              <h2 className="how-it-works__step-num">Book Your Free Trial</h2>

              <p className="how-it-works__step-desc">
                Share your name, location, the course you're interested in, and a time that works for you. It takes about 5 seconds.
              </p>
            </div>

            {/* Step 2 */}
            <div className="how-it-works__step">
              <div className="how-it-works__icon-wrapper">
                <span>2</span>
              </div>
              <h2 className="how-it-works__step-num">Meet the Tutor Who Fits Your Needs</h2>
              <p className="how-it-works__step-desc">
                We'll pair you with a certified tutor based on level, gender preference, and schedule, and get your first session on the calendar within 24 hours.
              </p>
            </div>

            {/* Step 3 */}
            <div className="how-it-works__step">
              <div className="how-it-works__icon-wrapper">
                <span>3</span>
              </div>
              <h2 className="how-it-works__step-num">Start Learning</h2>
              <p className="how-it-works__step-desc">
                Attend live one-on-one sessions, get feedback after every class, and follow a curriculum built to show real progress from the first week.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 2: Kids Quran Classes */}
      <section className="parallax-section kids-classes" id="kids-classes">
        <div className="parallax-section__container kids-classes__grid">
          {/* Left Column: Copy */}
          <div className="kids-classes__info">
            <h2 className="kids-classes__title">
              Learn Quran Online with Tajweed - Online Quran Classes for Kids & Adults
            </h2>

            <p className="kids-classes__desc">
              Raising kids with a real connection to the Quran isn't automatic in the US, and most parents already know that. Islamic education isn't built into the school system, weekend programs can be hit or miss, and not every local masjid has a dependable online option. That's the gap we set out to close.
            </p>

            <p className="kids-classes__desc">
              Our online Quran classes for kids are built around how children actually learn — patient, encouraging, and paced to the child in front of the tutor, not a script. Some kids show up excited from day one. Others need a few sessions to warm up. Our tutors are trained for both.
            </p>

            <p className="kids-classes__subheading">Here's what parents tell us, consistently:</p>
            <ul className="kids-classes__list">
              <li>Their kids start looking forward to class instead of avoiding it</li>
              <li>They can see real progress within the first month</li>
              <li>They trust that their child is being taught correctly, by someone qualified</li>
              <li>Flexible scheduling means Quran class fits around school and activities, not the other way around</li>
            </ul>

            <p className="kids-classes__desc-bold">
              We take students starting at age 4, usually beginning with our <a style={{ textDecoration: "none", color: "#006100" }} href="/">Noorani Qaida course</a> to make their foundation strong.
            </p>

            <a href="/free-trial/" className="kids-classes__cta-btn">
              <span>Get Free Trial For Classes</span>
              <svg className="kids-classes__cta-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.22 5.03a.75.75 0 111.06-1.06l5.5 5.5a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 11-1.06-1.06l4.168-4.17H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            </a>
          </div>

          {/* Right Column: Image */}
          <div className="kids-classes__image-container">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/g-5.jpg`}
              alt="Kid reading Quran on laptop"
              className="kids-classes__img"
            />
          </div>
        </div>
      </section>

      {/* SECTION 3: Perks and Benefits */}
      <section className="parallax-section perks-benefits" id="perks-benefits">
        <div className="parallax-section__container">
          <div className="perks-benefits__badge">
            <svg aria-hidden="true" className="perks-benefits__badge-icon" viewBox="0 0 640 512" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 480c0 17.67 14.33 32 32 32h64c17.67 0 32-14.33 32-32V160H0v320zm579.16-192c17.86-17.39 28.84-37.34 28.84-58.91 0-52.86-41.79-93.79-87.92-122.9-41.94-26.47-80.63-57.77-111.96-96.22L400 0l-8.12 9.97c-31.33 38.45-70.01 69.76-111.96 96.22C233.79 135.3 192 176.23 192 229.09c0 21.57 10.98 41.52 28.84 58.91h358.32zM608 320H192c-17.67 0-32 14.33-32 32v128c0 17.67 14.33 32 32 32h32v-64c0-17.67 14.33-32 32-32s32 14.33 32 32v64h64v-72c0-48 48-72 48-72s48 24 48 72v72h64v-64c0-17.67 14.33-32 32-32s32 14.33 32 32v64h32c17.67 0 32-14.33 32-32V352c0-17.67-14.33-32-32-32zM64 0S0 32 0 96v32h128V96c0-64-64-96-64-96z" />
            </svg>
            <span>Benefits</span>
          </div>

          <h2 className="perks-benefits__title">
            Perks and Benefits You’ll Get When Enrolling In Our Online Quran Classes
          </h2>

          <p className="perks-benefits__desc">
            Al Quran Lesson forms engaging learning experiences. We build genuine interest in Quranic education and offer comfortable environments where your kid feels supported and motivated. We combine traditional values with modern teaching approaches and present the best online Quran classes in the USA that build confidence and spiritual connection. Every session encourages curiosity and helps you develop a lasting appreciation for Islamic teachings through personalized attention and expert guidance.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ParallaxSections;

